<template>
  <div
    class="grid-card"
    :class="{
      dragging: isDragging,
      resizing: isResizing,
      'read-only': readOnly
    }"
    :data-card-id="card.id"
    :style="{
      left: card.left + '%',
      width: card.width + '%',
      backgroundColor: ruleColor || '#ffeaa7',
      transform: dragTransform || ''
    }"
    @click="!readOnly && handleCardClick($event)"
  >
    <a-pricing-strategy-card-rule-body :rule="card.rule" :mini="mini" />

    <!-- Handles de resize -->
    <div
      class="resize-handle-left"
      v-if="!readOnly"
      @mousedown.stop="handleResizeLeft($event)"
      :class="{ disabled: !canResizeLeft }"
    ></div>
    <div
      class="resize-handle-right"
      v-if="!readOnly"
      @mousedown.stop="handleResizeRight($event)"
      :class="{ disabled: !canResizeRight }"
    ></div>

    <!-- Acciones rápidas (visibles al hacer hover) -->
    <div class="card-actions" v-if="!readOnly">
      <v-btn
        icon
        x-small
        class="card-action"
        :title="$t('grid_card_copy')"
        @click.stop="$emit('copy-card', card)"
        @mousedown.stop
      >
        <v-icon x-small>mdi-content-copy</v-icon>
      </v-btn>
      <v-btn
        icon
        x-small
        class="card-action"
        :title="$t('grid_card_delete')"
        @click.stop="showDeleteStrategyRule()"
        @mousedown.stop
      >
        <v-icon x-small>mdi-delete</v-icon>
      </v-btn>
    </div>

    <!-- Handle para mover (arrastrar) -->
    <div
      class="drag-handle"
      v-if="!readOnly"
      :title="$t('grid_card_move')"
      @mousedown.stop="handleDragHandleMouseDown($event)"
      @click.stop
    >
      <v-icon size="20">mdi-drag</v-icon>
    </div>

    <!-- Delete rule -->
    <v-dialog
      v-model="show_delete_strategy_rule"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="500"
    >
      <v-card>
        <v-card-title center class="headline justify-center">
          {{ $t("are_you_sure") }}
        </v-card-title>
        <v-card-text class="text-center justify-center">
          <p>{{ $t("sure_delete_pricing_strategy_rule") }}</p>
          <template v-if="strategy_rule_to_delete">
            <div
              class="d-flex align-center justify-center font-weight-bold"
              style="gap:10px"
            >
              <div>{{ ruleToDeleteSummary }}</div>
            </div>
            <div
              v-if="strategy_rule_to_delete.strategies_count > 0"
              v-html="
                $t('sure_delete_pricing_strategy_rule_count', {
                  count: strategy_rule_to_delete.strategies_count
                })
              "
              class="font-weight-medium mt-4"
            ></div>
          </template>
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="red"
            rounded
            depressed
            text
            class="pl-5 pr-5"
            @click="deleteStrategyRule()"
          >
            {{ $t("delete") }}
          </v-btn>
          <v-btn
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="hideDeleteStrategyRule"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import aPricingStrategyCardRuleBody from "@/components/pricing-strategy/aPricingStrategyCardRuleBody.vue";
import {
  conditionResume,
  ruleSummaryPhrase
} from "@/components/pricing-strategy/pricingStrategyUtils";
import { mapState } from "vuex";

export default {
  name: "a-pricing-strategy-grid-rules-strategy-rule-card",
  components: {
    aPricingStrategyCardRuleBody
  },
  props: {
    card: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    isDragging: {
      type: Boolean,
      default: false
    },
    isResizing: {
      type: Boolean,
      default: false
    },
    dragTransform: {
      type: String,
      default: ""
    },
    canResizeLeft: {
      type: Boolean,
      default: true
    },
    canResizeRight: {
      type: Boolean,
      default: true
    },
    rowId: {
      type: [Number, String],
      required: true
    }
  },
  data: () => ({
    show_delete_strategy_rule: false,
    strategy_rule_to_delete: null,
    suppressNextClick: false
  }),
  computed: {
    ruleColor() {
      return this.card.rule.color;
    },
    // Primera condición de la primera ruta (shape v2: routes[].conditions[])
    ruleCondition() {
      const routes = this.card.rule.routes || [];
      const conditions = (routes[0] && routes[0].conditions) || [];
      return conditions[0] || { rule_type_code: "", settings: {} };
    },
    ruleConditionResume() {
      return conditionResume(this, this.ruleCondition);
    },
    // Compuesta en cliente. Antes se leía el `resume` del backend campo a
    // campo, que además de dar cadenas crudas reventaba si no venía.
    ruleToDeleteSummary() {
      const rule =
        this.strategy_rule_to_delete && this.strategy_rule_to_delete.rule;
      return rule ? ruleSummaryPhrase(this, rule) : "";
    },
    mini() {
      return this.card.width < 10;
    },
    ...mapState({
      strategy_id: state => state.pricingstrategy.strategy_id
    })
  },
  methods: {
    showDeleteStrategyRule() {
      this.show_delete_strategy_rule = true;
      this.strategy_rule_to_delete = this.card;
    },
    hideDeleteStrategyRule() {
      this.show_delete_strategy_rule = false;
      this.strategy_rule_to_delete = null;
    },
    deleteStrategyRule() {
      this.$emit("delete-card", this.card.id);
    },
    handleCardClick(e) {
      if (this.suppressNextClick) {
        this.suppressNextClick = false;
        return;
      }
      if (
        e.target.closest(".resize-handle-left") ||
        e.target.closest(".resize-handle-right") ||
        e.target.closest(".drag-handle") ||
        e.target.closest(".card-actions")
      ) {
        return;
      }
      this.$emit("edit-card", this.card.ruleId);
    },
    handleDragHandleMouseDown(e) {
      this.armClickSuppression();
      this.$emit("start-drag", e, this.card);
    },
    // Evita que el click que se dispara tras un resize/drag (mouseup en el
    // documento) acabe abriendo la edición de la regla.
    armClickSuppression() {
      this.suppressNextClick = true;
      const onUp = () => {
        window.removeEventListener("mouseup", onUp, true);
        setTimeout(() => {
          this.suppressNextClick = false;
        }, 0);
      };
      window.addEventListener("mouseup", onUp, true);
    },
    handleResizeLeft(e) {
      if (!this.canResizeLeft) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.armClickSuppression();
      this.$emit("start-resize-left", e, this.card);
    },
    handleResizeRight(e) {
      if (!this.canResizeRight) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      this.armClickSuppression();
      this.$emit("start-resize-right", e, this.card);
    }
  }
};
</script>

<style lang="scss" scoped>
.grid-card {
  position: absolute;
  top: 10px;
  bottom: 10px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  min-width: 5%;
  white-space: nowrap;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    .resize-handle-left,
    .resize-handle-right {
      opacity: 0.7;
    }
    .card-actions,
    .drag-handle {
      opacity: 1;
    }
  }
  &.dragging {
    border-color: #3498db;
    z-index: 1000;
    transform: rotate(2deg);
    opacity: 0.9;
  }
  &.resizing {
    border-color: #3498db;
  }
  &.read-only {
    cursor: default !important;
    pointer-events: auto;
    &:hover {
      transform: none !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    }
  }
}

.resize-handle-left,
.resize-handle-right {
  position: absolute;
  top: 0;
  width: 6px;
  height: 100%;
  background: #1576c7;
  cursor: ew-resize;
  opacity: 0;
  transition: opacity 0.2s ease;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    width: 16px;
    height: 100%;
    cursor: ew-resize;
  }
  &:hover {
    opacity: 1;
    background: #2980b9;
  }
  &.disabled {
    opacity: 0 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
    &:hover {
      opacity: 0 !important;
      background: #1576c7 !important;
    }
  }
}

.resize-handle-left {
  left: 0;
  border-radius: 8px 0 0 8px;
  &::before {
    left: -4px;
  }
}

.resize-handle-right {
  right: 0;
  border-radius: 0 8px 8px 0;
  &::before {
    right: -4px;
  }
}

// Acciones rápidas (copiar / eliminar) en la esquina superior derecha
.card-actions {
  position: absolute;
  top: 4px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 2px;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.card-action {
  border-radius: 50% !important;
  background: rgba(255, 255, 255, 0.55) !important;
  backdrop-filter: blur(4px);
  &:hover {
    background: rgba(255, 255, 255, 0.85) !important;
  }
}

// Handle para arrastrar/mover en la parte inferior central
.drag-handle {
  position: absolute;
  top: 4px;
  left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
  &:active {
    cursor: grabbing;
  }
  .v-icon {
    color: rgba(0, 0, 0, 0.55);
  }
}
</style>
